import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { API } from '../lib/api';
import { isAnswerCorrect, getCorrectIndices, getPassingPercent, requiredCorrect, getQuizQuestions } from '../utils/quizEval';

// YouTube Player Component for enhanced video tracking
const YouTubePlayer = ({ videoId, onProgress, onReady, onEnd, className = "" }) => {
  const playerRef = useRef(null);
  const playerInstanceRef = useRef(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [playerState, setPlayerState] = useState(-1); // -1: unstarted, 0: ended, 1: playing, 2: paused
  const intervalRef = useRef(null);

  useEffect(() => {
    console.log('YouTube Player component mounted with videoId:', videoId); // Debug log
    
    const initializePlayerWhenReady = () => {
      if (window.YT && window.YT.Player && window.YT.PlayerState) {
        console.log('YouTube API ready, initializing player'); // Debug log
        initializePlayer();
      } else {
        console.log('YouTube API not ready, waiting...'); // Debug log
        setTimeout(initializePlayerWhenReady, 100);
      }
    };

    // Initialize YouTube Player when component mounts
    initializePlayerWhenReady();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (playerInstanceRef.current && playerInstanceRef.current.destroy) {
        try {
          playerInstanceRef.current.destroy();
        } catch (error) {
          console.error('Error destroying player:', error);
        }
      }
    };
  }, [videoId]);

  const initializePlayer = () => {
    if (playerRef.current && videoId) {
      console.log('Creating YouTube Player instance'); // Debug log
      
      try {
        playerInstanceRef.current = new window.YT.Player(playerRef.current, {
          videoId: videoId,
          playerVars: {
            autoplay: 0,
            controls: 1,
            rel: 0,
            modestbranding: 1,
            showinfo: 0,
            fs: 1,
            cc_load_policy: 0,
            iv_load_policy: 3,
            autohide: 1,
            enablejsapi: 1,
          },
          events: {
            onReady: handlePlayerReady,
            onStateChange: handlePlayerStateChange,
            onError: handlePlayerError,
          },
        });
      } catch (error) {
        console.error('Error creating YouTube Player:', error);
      }
    }
  };

  const handlePlayerReady = (event) => {
    console.log('YouTube Player ready event triggered'); // Debug log
    setIsPlayerReady(true);
    onReady && onReady(event);
    
    // Start tracking progress immediately
    setTimeout(() => {
      startProgressTracking();
    }, 1000);
  };

  const handlePlayerStateChange = (event) => {
    console.log('YouTube Player state change:', event.data); // Debug log
    setPlayerState(event.data);
    
    if (window.YT && window.YT.PlayerState) {
      switch (event.data) {
        case window.YT.PlayerState.PLAYING:
          console.log('Video is playing, starting progress tracking'); // Debug log
          startProgressTracking();
          break;
        case window.YT.PlayerState.PAUSED:
          console.log('Video is paused, stopping progress tracking'); // Debug log
          stopProgressTracking();
          break;
        case window.YT.PlayerState.ENDED:
          console.log('Video has ended'); // Debug log
          stopProgressTracking();
          onEnd && onEnd();
          break;
        default:
          break;
      }
    }
  };

  const handlePlayerError = (event) => {
    console.error('YouTube Player Error:', event.data);
    stopProgressTracking();
  };

  const startProgressTracking = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    console.log('Starting progress tracking interval'); // Debug log
    intervalRef.current = setInterval(() => {
      if (playerInstanceRef.current && isPlayerReady) {
        try {
          const currentTime = playerInstanceRef.current.getCurrentTime();
          const duration = playerInstanceRef.current.getDuration();
          
          if (duration > 0) {
            const percentage = (currentTime / duration) * 100;
            console.log('Progress update:', percentage.toFixed(1), '%', currentTime, '/', duration); // Debug log
            onProgress && onProgress(percentage, currentTime, duration);
          }
        } catch (error) {
          console.error('Error getting video progress:', error);
        }
      }
    }, 1000); // Update every second
  };

  const stopProgressTracking = () => {
    if (intervalRef.current) {
      console.log('Stopping progress tracking interval'); // Debug log
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  return (
    <div className={`aspect-video ${className}`}>
      <div ref={playerRef} className="w-full h-full" />
    </div>
  );
};

// Image Viewer Component for module images
const ImageViewer = ({ images, onImageViewed, viewedImages }) => {
  if (!images || images.length === 0) return null;

  const handleImageClick = (index) => {
    console.log(`🖼️ Image ${index} clicked!`); // Debug log
    // Mark as viewed only on actual user click
    onImageViewed(index);
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 text-center">
        <div className="text-4xl mb-4">🖼️</div>
        <h3 className="text-xl font-bold text-blue-900 mb-2">Wichtige Bilder</h3>
        <p className="text-blue-700">
          Bitte alle Bilder vollständig ansehen, um das Modul abzuschließen
        </p>
        <p className="text-blue-600 text-sm mt-2">
          {Object.keys(viewedImages).length}/{images.length} Bilder angesehen
        </p>
      </div>

      {images.map((image, index) => {
        const isViewed = viewedImages[index];
        
        return (
          <div key={index} className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    isViewed 
                      ? 'bg-green-500 text-white' 
                      : 'bg-gray-300 text-gray-600'
                  }`}>
                    {isViewed ? '✓' : index + 1}
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">
                    {image.title || `Bild ${index + 1}`}
                  </h4>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isViewed 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {isViewed ? 'Angesehen' : 'Nicht angesehen'}
                </div>
              </div>
              {image.description && (
                <p className="text-gray-600 text-sm mt-2">{image.description}</p>
              )}
            </div>
            
            <div className="p-6">
              <div className="relative group">
                <img
                  src={image.url || image.src}
                  alt={image.alt || `Modul Bild ${index + 1}`}
                  className={`w-full max-h-96 object-contain rounded-lg cursor-pointer transition-all duration-300 ${
                    isViewed 
                      ? 'opacity-100' 
                      : 'hover:opacity-90 hover:scale-105'
                  }`}
                  onClick={() => handleImageClick(index)}
                  loading="eager"
                />
                
                {!isViewed && (
                  <div 
                    className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 rounded-lg group-hover:bg-opacity-10 transition-all duration-300 cursor-pointer"
                    onClick={() => handleImageClick(index)}
                  >
                    <div className="bg-white bg-opacity-90 rounded-full px-4 py-2 text-sm font-medium text-gray-900 pointer-events-none">
                      Zum Ansehen klicken
                    </div>
                  </div>
                )}
                
                {isViewed && (
                  <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
      
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
        <div className="text-center">
          <div className="text-2xl mb-2">👀</div>
          <h4 className="font-bold text-green-900 mb-1">Bild-Fortschritt</h4>
          <p className="text-green-700 text-sm mb-3">
            {Object.keys(viewedImages).length === images.length 
              ? 'Alle Bilder vollständig angesehen! Sie können fortfahren.' 
              : `Bitte alle Bilder ansehen (${Object.keys(viewedImages).length}/${images.length})`}
          </p>
          
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-green-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${(Object.keys(viewedImages).length / images.length) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Global slides cache - persists across component mounts/unmounts
const slidesCache = new Map();

const prefetchModuleSlides = async (moduleId) => {
  if (!moduleId || slidesCache.has(moduleId)) return;
  const token = localStorage.getItem('token');
  if (!token) return;
  try {
    const response = await fetch(`${API}/modules/${moduleId}/pdf-slides`, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    });
    if (response.ok) {
      const data = await response.json();
      if (data.slides && data.slides.length > 0) {
        slidesCache.set(moduleId, data.slides);
      }
    }
  } catch (e) { /* silent prefetch */ }
};

// Module Viewer Component for Learning
const ModuleViewer = ({ module, isOpen, onClose, onComplete, isPreviewMode = false }) => {
  // ALL hooks must be called before any conditional return (React hooks rule)
  const isCompleted = module?.completion_status === 'completed';
  
  // Content engagement tracking states
  const [hasReadToBottom, setHasReadToBottom] = useState(false);
  const [pdfFullyViewed, setPdfFullyViewed] = useState(false);
  const [videoFullyWatched, setVideoFullyWatched] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [contentScrollProgress, setContentScrollProgress] = useState(0);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizScore, setQuizScore] = useState(0);
  const [quizTotal, setQuizTotal] = useState(0);
  const [quizEvaluated, setQuizEvaluated] = useState(false);
  const [quizPassed, setQuizPassed] = useState(false);
  const [quizShowResults, setQuizShowResults] = useState(false);
  const [imagesViewed, setImagesViewed] = useState({});
  const [allImagesViewed, setAllImagesViewed] = useState(false);
  const [showCompletionWarning, setShowCompletionWarning] = useState(false);
  
  // TTS states - DISABLED FOR NOW (will be replaced with ElevenLabs in future)
  // const [isSpeaking, setIsSpeaking] = useState(false);
  // const [isPaused, setIsPaused] = useState(false);
  // const [speechRate, setSpeechRate] = useState(1.0);
  // const [speechVolume, setSpeechVolume] = useState(1.0);
  // const [speechProgress, setSpeechProgress] = useState(0);
  // const [currentUtterance, setCurrentUtterance] = useState(null);
  // const [supportsTTS, setSupportsTTS] = useState(false);
  // const [germanVoices, setGermanVoices] = useState([]);
  // const [selectedVoice, setSelectedVoice] = useState(null);
  
  // Ref for text content container
  const textContentRef = useRef(null);
  
  // Initialize TTS support and voices - DISABLED FOR NOW
  // Will be replaced with ElevenLabs integration in future
  /*
  useEffect(() => {
    if ('speechSynthesis' in window) {
      setSupportsTTS(true);
      
      // Load voices...
    }
  }, []);
  */
  
  // Additional state for PDF Slide Viewer
  const [pdfSlides, setPdfSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(1);
  const [slidesViewed, setSlidesViewed] = useState(new Set());
  const [allSlidesViewed, setAllSlidesViewed] = useState(false);
  const [slideTimer, setSlideTimer] = useState(0);
  const [canProceedToNextSlide, setCanProceedToNextSlide] = useState(false);
  const [isLoadingSlides, setIsLoadingSlides] = useState(module?.content_type === 'pdf');
  const [slideTimerActive, setSlideTimerActive] = useState(false);
  const [slideLoadError, setSlideLoadError] = useState(false);
  const [slideZoom, setSlideZoom] = useState(1); // Zoom: 1x, 1.5x, 2x
  
  // Fullscreen state for PDF slides (OLD - keeping for backward compatibility)
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Conversion progress state for professional loading screen
  const [conversionProgress, setConversionProgress] = useState(0);
  
  // Auto-save position tracking
  const [lastSavedPosition, setLastSavedPosition] = useState(null);
  const autoSaveTimerRef = useRef(null);
  
  // Auto-save function
  const savePosition = async (positionData) => {
    if (isPreviewMode) return; // Don't save in preview mode
    if (!onComplete) return; // Read-only mode (Vertriebsleiter) – don't save position
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      await axios.put(
        `${API}/progress/${module.id}/position`,
        positionData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      setLastSavedPosition(positionData);
      console.log('💾 Position auto-saved:', positionData);
    } catch (error) {
      console.error('Error saving position:', error);
    }
  };
  
  // Auto-save current slide position (PDF)
  useEffect(() => {
    if (module && module.content_type === 'pdf' && currentSlide > 0 && pdfSlides.length > 0) {
      // Clear previous timer
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      
      // Save after 3 seconds of being on this slide
      autoSaveTimerRef.current = setTimeout(() => {
        savePosition({ type: 'slide', value: currentSlide });
      }, 3000);
    }
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [currentSlide, module, pdfSlides.length]);
  
  // Load saved video position when module opens
  useEffect(() => {
    const loadVideoPosition = async () => {
      if (module && module.content_type === 'video' && isOpen) {
        try {
          const token = localStorage.getItem('token');
          if (!token) return;
          
          const response = await axios.get(`${API}/progress/${module.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.data && response.data.current_position && response.data.current_position.type === 'video') {
            const savedTime = response.data.current_position.value;
            console.log(`📍 Resuming video at ${savedTime}s`);
            
            // Wait for video element to be available
            setTimeout(() => {
              const videoElement = document.querySelector('video');
              if (videoElement) {
                videoElement.currentTime = savedTime;
              }
            }, 500);
          }
        } catch (error) {
          console.error('Error loading video position:', error);
        }
      }
    };
    
    loadVideoPosition();
  }, [module, isOpen]);
  
  // Simulate conversion progress when loading slides
  useEffect(() => {
    if (isLoadingSlides) {
      setConversionProgress(0);
      const progressInterval = setInterval(() => {
        setConversionProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          // Simulate realistic conversion progress with varying speeds
          const increment = Math.random() * 15 + 5; // 5-20% increments
          return Math.min(prev + increment, 100);
        });
      }, 800); // Update every 800ms for smooth progress
      
      return () => clearInterval(progressInterval);
    }
  }, [isLoadingSlides]);
  
  // Timer effect for PDF slides (3-second anti-skip)
  useEffect(() => {
    let timer;
    if (slideTimerActive && slideTimer > 0) {
      timer = setTimeout(() => {
        setSlideTimer(prev => {
          const newTime = prev - 1;
          if (newTime <= 0) {
            setCanProceedToNextSlide(true);
            setSlideTimerActive(false);
            // Mark current slide as viewed when timer completes
            setSlidesViewed(prev => {
              const updated = new Set(prev);
              updated.add(currentSlide);
              return updated;
            });
          }
          return newTime;
        });
      }, 1000);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [slideTimer, slideTimerActive, currentSlide]);

  // Keyboard navigation for fullscreen mode
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!isFullscreen) return;

      switch (event.key) {
        case 'Escape':
          setIsFullscreen(false);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          if (currentSlide > 1) {
            setCurrentSlide(prev => prev - 1);
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (canProceedToNextSlide && currentSlide < pdfSlides.length) {
            setCurrentSlide(prev => prev + 1);
            setCanProceedToNextSlide(false);
            setSlideTimer(30);
            setSlideTimerActive(true);
          }
          break;
        case ' ': // Spacebar
          event.preventDefault();
          if (canProceedToNextSlide && currentSlide < pdfSlides.length) {
            setCurrentSlide(prev => prev + 1);
            setCanProceedToNextSlide(false);
            setSlideTimer(30);
            setSlideTimerActive(true);
          }
          break;
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isFullscreen, currentSlide, canProceedToNextSlide, pdfSlides.length]);

  // Load PDF slides when PDF module is opened (cache-first, then fetch)
  useEffect(() => {
    let cancelled = false;
    
    const loadPdfSlides = async () => {
      if (!module || module.content_type !== 'pdf') return;
      
      const token = localStorage.getItem('token');
      if (!token) {
        setIsLoadingSlides(false);
        return;
      }
      
      setSlideLoadError(false);
      
      // Check cache first - instant load
      const cached = slidesCache.get(module.id);
      if (cached && cached.length > 0) {
        setPdfSlides(cached);
        setIsLoadingSlides(false);
      } else {
        setIsLoadingSlides(true);
      }
      
      try {
        let slides = cached;
        if (!slides || slides.length === 0) {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000);
          
          const response = await fetch(`${API}/modules/${module.id}/pdf-slides`, {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            signal: controller.signal
          });
          clearTimeout(timeoutId);
          
          if (cancelled) return;
          
          if (response.ok) {
            const slidesData = await response.json();
            if (slidesData.slides && slidesData.slides.length > 0) {
              slides = slidesData.slides;
              slidesCache.set(module.id, slides);
              if (!cancelled) setPdfSlides(slides);
            }
          }
        }
        
        if (cancelled) return;
        
        if (slides && slides.length > 0) {
          try {
            const progressResponse = await fetch(`${API}/progress/${module.id}`, {
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (cancelled) return;
            
            if (progressResponse.ok) {
              const progressData = await progressResponse.json();
              if (progressData && progressData.current_position && progressData.current_position.type === 'slide') {
                const savedSlide = progressData.current_position.value;
                setCurrentSlide(savedSlide);
                const viewedSlides = new Set();
                for (let i = 1; i < savedSlide; i++) viewedSlides.add(i);
                setSlidesViewed(viewedSlides);
              } else {
                setCurrentSlide(1);
                setSlidesViewed(new Set());
              }
            } else {
              setCurrentSlide(1);
              setSlidesViewed(new Set());
            }
          } catch (e) {
            if (!cancelled) { setCurrentSlide(1); setSlidesViewed(new Set()); }
          }
          
          if (!cancelled) {
            setAllSlidesViewed(false);
            setCanProceedToNextSlide(false);
            setSlideTimer(30);
            setSlideTimerActive(true);
          }
        } else if (!cancelled) {
          setSlideLoadError(true);
        }
      } catch (error) {
        if (!cancelled) setSlideLoadError(true);
      } finally {
        if (!cancelled) setIsLoadingSlides(false);
      }
    };

    loadPdfSlides();
    return () => { cancelled = true; };
  }, [module?.id, module?.content_type]);

  // Convert PDF to slides (admin only)
  const convertPdfToSlides = async () => {
    const token = localStorage.getItem('token');
    if (!token || !module) return;
    
    setIsLoadingSlides(true);
    try {
      const response = await fetch(`${API}/modules/${module.id}/pdf-slides`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const slidesData = await response.json();
        setPdfSlides(slidesData.slides || []);
        setCurrentSlide(1);
        setSlidesViewed(new Set());
        setAllSlidesViewed(false);
        setCanProceedToNextSlide(false);
        setSlideTimer(30);
        setSlideTimerActive(true);
        console.log('PDF erfolgreich in Slides konvertiert!');
      } else {
        const errorData = await response.json();
        console.error(`Fehler bei PDF-Konvertierung: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Error converting PDF to slides:', error);
      console.error('Fehler bei PDF-Konvertierung');
    } finally {
      setIsLoadingSlides(false);
    }
  };

  // Navigate to next slide
  const goToNextSlide = () => {
    if (!canProceedToNextSlide) return;
    
    // Mark current slide as viewed
    const newSlidesViewed = new Set(slidesViewed);
    newSlidesViewed.add(currentSlide);
    setSlidesViewed(newSlidesViewed);
    
    // Update progress on backend
    updateSlideProgress(currentSlide, true);
    
    // Check if all slides viewed
    if (newSlidesViewed.size >= pdfSlides.length) {
      setAllSlidesViewed(true);
      setPdfFullyViewed(true);
    }
    
    // If this is the last slide, complete the module directly
    if (currentSlide >= pdfSlides.length) {
      console.log('📚 Last PDF slide completed, completing module...');
      if (onComplete) {
        onComplete(module, {});
        onClose();
      } else {
        // Read-only mode
        onClose();
      }
      return;
    }
    
    // Move to next slide
    const nextSlide = currentSlide + 1;
    setCurrentSlide(nextSlide);
    setSlideZoom(1); // Reset zoom on slide change
    setCanProceedToNextSlide(false);
    setSlideTimer(30);
    setSlideTimerActive(true);
  };

  // Navigate to previous slide
  const goToPreviousSlide = () => {
    if (currentSlide <= 1) return;
    
    const prevSlide = currentSlide - 1;
    setCurrentSlide(prevSlide);
    
    // If going back to a slide that was already viewed, allow immediate progression
    if (slidesViewed.has(prevSlide)) {
      setCanProceedToNextSlide(true);
      setSlideTimer(0);
      setSlideTimerActive(false);
    } else {
      setCanProceedToNextSlide(false);
      setSlideTimer(30);
      setSlideTimerActive(true);
    }
  };

  // Update slide progress on backend
  const updateSlideProgress = async (slideNumber, viewed) => {
    const token = localStorage.getItem('token');
    if (!token || !module) return;
    
    try {
      await fetch(`${API}/modules/${module.id}/pdf-slides/progress`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          slide_number: slideNumber,
          viewed: viewed
        })
      });
    } catch (error) {
      console.error('Error updating slide progress:', error);
    }
  };

  // Mark slide as important (admin only)
  const markSlideImportant = async (slideId, isImportant) => {
    const token = localStorage.getItem('token');
    if (!token || !module) return;
    
    try {
      const response = await fetch(`${API}/modules/${module.id}/pdf-slides/${slideId}/important?is_important=${isImportant}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        // Update local state
        setPdfSlides(slides => 
          slides.map(slide => 
            slide.id === slideId 
              ? { ...slide, is_important: isImportant }
              : slide
          )
        );
        console.log(`Slide als ${isImportant ? 'wichtig' : 'normal'} markiert`);
      }
    } catch (error) {
      console.error('Error marking slide as important:', error);
      console.error('Fehler beim Markieren des Slides');
    }
  };

  // Reset tracking states when module changes
  useEffect(() => {
    if (module) {
      setHasReadToBottom(false);
      setPdfFullyViewed(false);
      setVideoFullyWatched(false);
      setQuizCompleted(false);
      setContentScrollProgress(0);
      setVideoProgress(0);
      setVideoDuration(0);
      setQuizAnswers({});
      setQuizScore(0);
      setQuizTotal(0);
      setQuizEvaluated(false);
      setQuizPassed(false);
      setQuizShowResults(false);
      setImagesViewed({});
      setAllImagesViewed(false);
      setShowCompletionWarning(false);
      
      // Reset PDF slide states - DON'T reset pdfSlides here, the load effect handles it
      setCurrentSlide(1);
      setSlidesViewed(new Set());
      setAllSlidesViewed(false);
      setSlideTimer(0);
      setCanProceedToNextSlide(false);
      setSlideTimerActive(false);
      setIsLoadingSlides(false);
      setSlideLoadError(false);
      
      // Stop any ongoing speech - DISABLED FOR NOW
      // if (isSpeaking) {
      //   stopSpeaking();
      // }
    }
  }, [module?.id]);
  
  // Check text scrollability when module content loads
  useEffect(() => {
    if (module && module.content_type === 'text' && textContentRef.current) {
      // Use setTimeout to ensure content is rendered
      setTimeout(() => {
        checkTextScrollability(textContentRef.current);
      }, 100);
    }
  }, [module?.content, module?.content_type]);
  
  // Early return AFTER all hooks (React hooks must be called in same order every render)
  if (!isOpen || !module) return null;
  
  // Cleanup speech when component unmounts - DISABLED FOR NOW
  // useEffect(() => {
  //   return () => {
  //     if (speechSynthesis.speaking) {
  //       speechSynthesis.cancel();
  //     }
  //   };
  // }, []);
  
  // Text-to-Speech Functions - DISABLED FOR NOW
  // Will be replaced with ElevenLabs integration in future
  /*
  const convertMarkdownToText = (markdown) => {
    // ... TTS functions disabled ...
  };
  
  const startSpeaking = () => {
    // ... TTS functions disabled ...
  };
  
  const pauseSpeaking = () => {
    // ... TTS functions disabled ...
  };
  
  const resumeSpeaking = () => {
    // ... TTS functions disabled ...
  };
  
  const stopSpeaking = () => {
    // ... TTS functions disabled ...
  };
  */
  
  // Render TTS Controls - DISABLED FOR NOW
  const renderTTSControls = () => {
    // TTS functionality disabled - will be replaced with ElevenLabs in future
    return null;
  };

  // Check if user can complete based on content type
  const canComplete = () => {
    if (isCompleted) return false;
    
    // Check if all required content including images is viewed
    return isAllContentViewed();
  };

  // Get specific completion requirement message
  const getCompletionRequirement = () => {
    const moduleImages = getModuleImages();
    const hasImages = moduleImages.length > 0;
    const viewedImages = Object.keys(imagesViewed).length;
    
    // Check image requirement first
    if (hasImages && !allImagesViewed) {
      return `⚠️ Bitte alle Bilder im Modul vollständig ansehen (${viewedImages}/${moduleImages.length}), bevor du fortfahren kannst.`;
    }
    
    switch (module.content_type) {
      case 'text':
        if (!hasReadToBottom) {
          return 'Bitte lesen Sie den Text vollständig bis zum Ende';
        }
        return hasImages ? 'Text und alle Bilder vollständig angesehen' : 'Text vollständig gelesen';
      case 'video':
        if (!videoFullyWatched) {
          return 'Bitte schauen Sie das Video vollständig an (mindestens 95%)';
        }
        return hasImages ? 'Video und alle Bilder vollständig angesehen' : 'Video vollständig angesehen';
      case 'document':
        if (!pdfFullyViewed) {
          return 'Bitte öffnen und betrachten Sie das Dokument vollständig';
        }
        return hasImages ? 'Dokument und alle Bilder vollständig angesehen' : 'Dokument vollständig geladen';
      case 'pdf':
        // Special handling for PDF slides
        if (pdfSlides.length > 0) {
          if (!allSlidesViewed) {
            return `Bitte alle PDF-Slides ansehen (${slidesViewed.size}/${pdfSlides.length})`;
          }
          return hasImages ? 'Alle PDF-Slides und Bilder vollständig angesehen' : 'Alle PDF-Slides vollständig angesehen';
        } else {
          // Fallback to regular PDF handling
          if (!pdfFullyViewed) {
            return 'Bitte öffnen und betrachten Sie das PDF vollständig';
          }
          return hasImages ? 'PDF und alle Bilder vollständig angesehen' : 'PDF vollständig geladen';
        }
      case 'quiz':
        if (quizEvaluated && quizPassed) {
          return hasImages ? 'Quiz und alle Bilder erfolgreich abgeschlossen' : 'Quiz erfolgreich abgeschlossen';
        } else if (quizEvaluated && !quizPassed) {
          return 'Du hast leider nicht bestanden. Bitte wiederhole das Modul, um fortzufahren.';
        } else if (Object.keys(quizAnswers).length === 0) {
          return hasImages ? 'Bitte alle Bilder ansehen und Quiz-Fragen beantworten' : 'Bitte beantworten Sie alle Quiz-Fragen';
        } else {
          const allAnswered = Object.keys(quizAnswers).length >= quizTotal;
          return allAnswered 
            ? 'Alle Fragen beantwortet. Klicken Sie auf "Modul abschließen" für die Auswertung.'
            : (hasImages ? 'Bitte alle Bilder ansehen und Quiz-Fragen beantworten' : 'Bitte beantworten Sie alle Quiz-Fragen');
        }
      default:
        return hasImages ? 'Bitte alle Bilder vollständig ansehen' : 'Modul bereit zum Abschluss';
    }
  };

  // Handle completion button click with validation
  const handleCompletionAttempt = async () => {
    // Prevent completion in preview mode
    if (isPreviewMode) {
      console.log('🔒 Admin preview mode - completion blocked');
      return;
    }
    
    console.log('🚀 handleCompletionAttempt called for module:', module.title);
    console.log('🔍 Module type:', module.content_type);
    console.log('🔍 Can complete:', canComplete());
    console.log('🔍 Is completed:', isCompleted);
    
    // Special handling for quiz modules
    if (module.content_type === 'quiz') {
      console.log('📝 Processing quiz module completion...');
      if (canComplete()) {
        // Evaluate the quiz
        const passed = evaluateQuiz();
        console.log('🎯 Quiz evaluation result:', passed);
        
        if (passed) {
          // Quiz passed locally – the server result is authoritative
          console.log('✅ Quiz passed, calling onComplete...');
          if (onComplete) {
            const serverOk = await onComplete(module, quizAnswers);
            if (serverOk === false) {
              setQuizPassed(false);
              setQuizShowResults(false);
            }
          }
          // Don't close - show detailed results
        } else {
          console.log('❌ Quiz failed, not completing module');
          // Quiz failed - show failure message, don't complete
          // The UI will show the failure message based on quizEvaluated and quizPassed states
        }
      } else {
        console.log('⚠️ Cannot complete quiz, showing warning');
        setShowCompletionWarning(true);
        setTimeout(() => setShowCompletionWarning(false), 5000);
      }
    } else {
      // Normal module completion
      console.log('📖 Processing normal module completion...');
      if (!onComplete) {
        // Read-only mode (e.g. Vertriebsleiter) – just close
        onClose();
        return;
      }
      if (canComplete()) {
        console.log('✅ Module can be completed, calling onComplete...');
        onComplete(module, {});
        onClose();
      } else {
        console.log('⚠️ Cannot complete module, showing warning');
        setShowCompletionWarning(true);
        setTimeout(() => setShowCompletionWarning(false), 5000);
      }
    }
  };

  // Text scroll tracking
  const handleTextScroll = (e) => {
    const element = e.target;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;
    
    // Check if content is short enough to not need scrolling
    if (scrollHeight <= clientHeight + 10) {
      // Content fits in container without scrolling - mark as read immediately
      setContentScrollProgress(100);
      setHasReadToBottom(true);
      return;
    }
    
    // Calculate scroll progress for scrollable content
    const scrollable = scrollHeight - clientHeight;
    const progress = scrollable > 0 ? Math.round((scrollTop / scrollable) * 100) : 100;
    setContentScrollProgress(Math.min(progress, 100));
    
    // Check if scrolled to bottom (allowing for 10px tolerance)
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
    
    if (isAtBottom) {
      setHasReadToBottom(true);
    }
  };

  // Check if text content needs scrolling on load
  const checkTextScrollability = (element) => {
    if (!element) return;
    
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;
    
    // If content fits without scrolling, mark as read immediately
    if (scrollHeight <= clientHeight + 10) {
      setContentScrollProgress(100);
      setHasReadToBottom(true);
    }
  };

  // PDF load tracking
  const handlePdfLoad = () => {
    // Simple PDF load tracking - assumes user has viewed if iframe loaded
    // In a real implementation, you might want to track page views
    setPdfFullyViewed(true);
  };

  // Video tracking functions
  const handleVideoTimeUpdate = (e) => {
    const video = e.target;
    const progress = (video.currentTime / video.duration) * 100;
    setVideoProgress(progress);
    
    // Consider video fully watched if 95% or more is watched
    if (progress >= 95) {
      setVideoFullyWatched(true);
    }
    
    // Auto-save video position every 5 seconds
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(() => {
      savePosition({ type: 'video', value: video.currentTime });
    }, 5000);
  };

  const handleVideoLoadedMetadata = (e) => {
    const video = e.target;
    setVideoDuration(video.duration);
  };

  const handleVideoEnded = () => {
    setVideoFullyWatched(true);
    setVideoProgress(100);
  };

  // Quiz completion tracking - supports both single and multiple choice
  const handleQuizAnswer = (questionIndex, answerIndex, questionType = 'single') => {
    const newAnswers = { ...quizAnswers };
    
    if (questionType === 'multiple') {
      // Multiple choice: toggle selection in array
      if (!newAnswers[questionIndex]) {
        newAnswers[questionIndex] = [];
      }
      
      const currentAnswers = [...newAnswers[questionIndex]];
      const answerIndexInArray = currentAnswers.indexOf(answerIndex);
      
      if (answerIndexInArray > -1) {
        // Remove answer if already selected
        currentAnswers.splice(answerIndexInArray, 1);
      } else {
        // Add answer if not selected
        currentAnswers.push(answerIndex);
      }
      
      newAnswers[questionIndex] = currentAnswers.sort(); // Keep sorted for consistency
    } else {
      // Single choice: replace with single answer
      newAnswers[questionIndex] = [answerIndex];
    }
    
    setQuizAnswers(newAnswers);
    
    // Get quiz data for total count
    const quizData = { questions: getQuizQuestions(module) };
    
    const totalQuestions = quizData.questions?.length || 0;
    const answeredQuestions = Object.keys(newAnswers).filter(key => 
      newAnswers[key] && newAnswers[key].length > 0
    ).length;
    
    setQuizTotal(totalQuestions);
    
    // Only check if all questions are answered (no score calculation yet)
    if (answeredQuestions >= totalQuestions && totalQuestions > 0) {
      setQuizCompleted(true);
    } else {
      setQuizCompleted(false);
    }
  };
  
  // Quiz evaluation function - called when "Modul abschließen" is clicked
  const evaluateQuiz = () => {
    const quizData = { questions: getQuizQuestions(module) };
    
    const totalQuestions = quizData.questions?.length || 0;
    let correctAnswers = 0;
    
    if (quizData.questions) {
      quizData.questions.forEach((question, index) => {
        if (isAnswerCorrect(question, quizAnswers[index] || [])) {
          correctAnswers++;
        }
      });
    }
    
    setQuizScore(correctAnswers);
    setQuizTotal(totalQuestions);
    setQuizEvaluated(true);
    
    const passed = correctAnswers >= requiredCorrect(totalQuestions, getPassingPercent(module));
    
    setQuizPassed(passed);
    setQuizShowResults(passed); // Only show detailed results if passed
    
    return passed;
  };
  
  // Reset quiz function - called when quiz failed
  const resetQuiz = () => {
    console.log('Resetting quiz...'); // Debug log
    setQuizAnswers({});
    setQuizScore(0);
    setQuizTotal(0);
    setQuizEvaluated(false);
    setQuizPassed(false);
    setQuizShowResults(false);
    setQuizCompleted(false);
    setShowCompletionWarning(false);
    
    // Also reset quiz total to trigger re-initialization
    const quizData = { questions: getQuizQuestions(module) };
    
    const totalQuestions = quizData.questions?.length || 0;
    setQuizTotal(totalQuestions);
    
    console.log('Quiz reset complete. Total questions:', totalQuestions); // Debug log
  };

  // Image viewing tracking
  const handleImageViewed = (imageIndex) => {
    console.log(`🖼️ handleImageViewed called for image ${imageIndex}`); // Debug log
    
    const newImagesViewed = { ...imagesViewed };
    newImagesViewed[imageIndex] = true;
    setImagesViewed(newImagesViewed);
    
    console.log(`📊 Images viewed state updated:`, newImagesViewed); // Debug log
    
    // Check if all images are viewed
    const moduleImages = getModuleImages();
    const totalImages = moduleImages.length;
    const viewedCount = Object.keys(newImagesViewed).length;
    
    console.log(`📈 Progress: ${viewedCount}/${totalImages} images viewed`); // Debug log
    
    if (viewedCount >= totalImages && totalImages > 0) {
      console.log('✅ All images viewed! Setting allImagesViewed to true'); // Debug log
      setAllImagesViewed(true);
    }
  };

  // Get images from module content
  const getModuleImages = () => {
    if (!module.images) return [];
    
    try {
      // Module images can be an array or JSON string
      if (Array.isArray(module.images)) {
        return module.images;
      } else if (typeof module.images === 'string') {
        return JSON.parse(module.images);
      }
    } catch (error) {
      console.error('Error parsing module images:', error);
    }
    
    return [];
  };

  // Check if all required content is viewed (including images and PDF slides)
  const isAllContentViewed = () => {
    const moduleImages = getModuleImages();
    const hasImages = moduleImages.length > 0;
    
    switch (module.content_type) {
      case 'text':
        return hasImages ? (hasReadToBottom && allImagesViewed) : hasReadToBottom;
      case 'video':
        return hasImages ? (videoFullyWatched && allImagesViewed) : videoFullyWatched;
      case 'document':
        return hasImages ? (pdfFullyViewed && allImagesViewed) : pdfFullyViewed;
      case 'pdf':
        // For PDF modules, check if all slides are viewed (if slides exist) or fallback to normal PDF viewing
        if (pdfSlides.length > 0) {
          const allSlidesViewedCondition = hasImages ? (allSlidesViewed && allImagesViewed) : allSlidesViewed;
          return allSlidesViewedCondition;
        } else {
          // Fallback to normal PDF viewing if no slides exist
          return hasImages ? (pdfFullyViewed && allImagesViewed) : pdfFullyViewed;
        }
      case 'quiz':
        return hasImages ? (quizCompleted && allImagesViewed) : quizCompleted;
      default:
        return hasImages ? allImagesViewed : true;
    }
  };

  const getCompletionButtonText = () => {
    if (isPreviewMode) return '👁️ Vorschau-Modus';
    if (isCompleted) return 'Bereits abgeschlossen';
    
    switch (module.content_type) {
      case 'text':
        return hasReadToBottom ? 'Modul abschließen' : 'Bitte zuerst vollständig lesen';
      case 'document':
        return pdfFullyViewed ? 'Modul abschließen' : 'Bitte PDF vollständig laden';
      case 'pdf':
        // Special handling for PDF slides
        if (pdfSlides.length > 0) {
          return allSlidesViewed ? 'Modul abschließen' : 'Bitte alle Slides ansehen';
        } else {
          return pdfFullyViewed ? 'Modul abschließen' : 'Bitte PDF vollständig laden';
        }
      case 'video':
        return videoFullyWatched ? 'Modul abschließen' : 'Bitte Video vollständig ansehen';
      case 'quiz':
        if (quizEvaluated && quizPassed) {
          return 'Quiz erfolgreich abgeschlossen';
        } else if (quizEvaluated && !quizPassed) {
          return 'Quiz wiederholen';
        } else if (Object.keys(quizAnswers).length === 0) {
          return 'Bitte alle Fragen beantworten';
        } else {
          const allAnswered = Object.keys(quizAnswers).length >= quizTotal;
          return allAnswered ? 'Modul abschließen' : 'Bitte alle Fragen beantworten';
        }
      default:
        return 'Modul abschließen';
    }
  };

  const getProgressIndicator = () => {
    if (isCompleted) return null;
    
    const moduleImages = getModuleImages();
    const hasImages = moduleImages.length > 0;
    
    switch (module.content_type) {
      case 'text':
        return (
          <div className="space-y-2">
            <div className="flex items-center space-x-2 text-sm">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${contentScrollProgress}%` }}
                />
              </div>
              <span className="text-gray-600">{contentScrollProgress.toFixed(0)}%</span>
              {hasReadToBottom && (
                <span className="text-green-600 font-medium">✓ vollständig gelesen</span>
              )}
            </div>
            {hasImages && (
              <div className="flex items-center space-x-2 text-sm">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(Object.keys(imagesViewed).length / moduleImages.length) * 100}%` }}
                  />
                </div>
                <span className="text-gray-600">{Object.keys(imagesViewed).length}/{moduleImages.length} Bilder</span>
                {allImagesViewed && (
                  <span className="text-blue-600 font-medium">✓ alle Bilder angesehen</span>
                )}
              </div>
            )}
          </div>
        );
      case 'document':
        return (
          <div className="space-y-2">
            <div className="flex items-center space-x-2 text-sm">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${pdfFullyViewed ? 100 : 0}%` }}
                />
              </div>
              <span className="text-gray-600">{pdfFullyViewed ? '100%' : '0%'}</span>
              {pdfFullyViewed && (
                <span className="text-purple-600 font-medium">✓ vollständig geladen</span>
              )}
            </div>
            {hasImages && (
              <div className="flex items-center space-x-2 text-sm">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(Object.keys(imagesViewed).length / moduleImages.length) * 100}%` }}
                  />
                </div>
                <span className="text-gray-600">{Object.keys(imagesViewed).length}/{moduleImages.length} Bilder</span>
                {allImagesViewed && (
                  <span className="text-blue-600 font-medium">✓ alle Bilder angesehen</span>
                )}
              </div>
            )}
          </div>
        );
      case 'pdf':
        // Special handling for PDF slides
        if (pdfSlides.length > 0) {
          return (
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-sm">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(slidesViewed.size / pdfSlides.length) * 100}%` }}
                  />
                </div>
                <span className="text-gray-600">{slidesViewed.size}/{pdfSlides.length} Slides</span>
                {allSlidesViewed && (
                  <span className="text-blue-600 font-medium">✓ alle Slides angesehen</span>
                )}
              </div>
              {hasImages && (
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(Object.keys(imagesViewed).length / moduleImages.length) * 100}%` }}
                    />
                  </div>
                  <span className="text-gray-600">{Object.keys(imagesViewed).length}/{moduleImages.length} Bilder</span>
                  {allImagesViewed && (
                    <span className="text-purple-600 font-medium">✓ alle Bilder angesehen</span>
                  )}
                </div>
              )}
            </div>
          );
        } else {
          // Fallback to regular PDF progress
          return (
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-sm">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${pdfFullyViewed ? 100 : 0}%` }}
                  />
                </div>
                <span className="text-gray-600">{pdfFullyViewed ? '100%' : '0%'}</span>
                {pdfFullyViewed && (
                  <span className="text-purple-600 font-medium">✓ vollständig geladen</span>
                )}
              </div>
              {hasImages && (
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(Object.keys(imagesViewed).length / moduleImages.length) * 100}%` }}
                    />
                  </div>
                  <span className="text-gray-600">{Object.keys(imagesViewed).length}/{moduleImages.length} Bilder</span>
                  {allImagesViewed && (
                    <span className="text-blue-600 font-medium">✓ alle Bilder angesehen</span>
                  )}
                </div>
              )}
            </div>
          );
        }
      case 'video':
        return (
          <div className="space-y-2">
            <div className="flex items-center space-x-2 text-sm">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-red-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${videoProgress}%` }}
                />
              </div>
              <span className="text-gray-600">{videoProgress.toFixed(1)}%</span>
              {videoFullyWatched && (
                <span className="text-red-600 font-medium">✓ vollständig angesehen</span>
              )}
            </div>
            {hasImages && (
              <div className="flex items-center space-x-2 text-sm">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(Object.keys(imagesViewed).length / moduleImages.length) * 100}%` }}
                  />
                </div>
                <span className="text-gray-600">{Object.keys(imagesViewed).length}/{moduleImages.length} Bilder</span>
                {allImagesViewed && (
                  <span className="text-blue-600 font-medium">✓ alle Bilder angesehen</span>
                )}
              </div>
            )}
          </div>
        );
      case 'quiz':
        const totalQuestions = getQuizQuestions(module).length;
        const answeredQuestions = Object.keys(quizAnswers).length;
        const quizProgress = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;
        
        return (
          <div className="space-y-2">
            <div className="flex items-center space-x-2 text-sm">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${quizProgress}%` }}
                />
              </div>
              <span className="text-gray-600">{answeredQuestions}/{totalQuestions} Fragen</span>
              {quizEvaluated && quizPassed && (
                <span className="text-green-600 font-medium">✓ bestanden</span>
              )}
              {quizEvaluated && !quizPassed && (
                <span className="text-red-600 font-medium">✗ nicht bestanden</span>
              )}
            </div>
            
            {quizEvaluated && (
              <div className="flex items-center space-x-2 text-sm">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      quizPassed ? 'bg-green-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${quizTotal > 0 ? (quizScore / quizTotal) * 100 : 0}%` }}
                  />
                </div>
                <span className={`font-medium ${
                  quizPassed ? 'text-green-600' : 'text-red-600'
                }`}>
                  {quizScore}/{quizTotal} korrekt ({quizTotal > 0 ? ((quizScore / quizTotal) * 100).toFixed(1) : 0}%)
                </span>
              </div>
            )}
            
            {hasImages && (
              <div className="flex items-center space-x-2 text-sm">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(Object.keys(imagesViewed).length / moduleImages.length) * 100}%` }}
                  />
                </div>
                <span className="text-gray-600">{Object.keys(imagesViewed).length}/{moduleImages.length} Bilder</span>
                {allImagesViewed && (
                  <span className="text-blue-600 font-medium">✓ alle Bilder angesehen</span>
                )}
              </div>
            )}
          </div>
        );
      default:
        if (hasImages) {
          return (
            <div className="flex items-center space-x-2 text-sm">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(Object.keys(imagesViewed).length / moduleImages.length) * 100}%` }}
                />
              </div>
              <span className="text-gray-600">{Object.keys(imagesViewed).length}/{moduleImages.length} Bilder</span>
              {allImagesViewed && (
                <span className="text-blue-600 font-medium">✓ alle Bilder angesehen</span>
              )}
            </div>
          );
        }
        return null;
    }
  };

  const renderContent = () => {
    switch (module.content_type) {
      case 'text':
        return (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <div className="text-4xl mb-4">📖</div>
              <h3 className="text-xl font-bold text-green-900 mb-2">Lese-Modul</h3>
              <p className="text-green-700">Lesen Sie den Inhalt sorgfältig durch</p>
            </div>
            
            {/* Main Content First - Text */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-lg">
              <div 
                ref={textContentRef}
                className="p-8 max-h-96 overflow-y-auto"
                onScroll={handleTextScroll}
                style={{ scrollbarWidth: 'thin' }}
              >
                <div className="prose max-w-none prose-lg prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:text-gray-900 prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded">
                  <ReactMarkdown
                    components={{
                      code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        return !inline && match ? (
                          <SyntaxHighlighter
                            style={tomorrow}
                            language={match[1]}
                            PreTag="div"
                            {...props}
                          >
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        ) : (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        );
                      }
                    }}
                  >
                    {module.content || 'Kein Inhalt verfügbar.'}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
            
            {/* Module Images - After Main Content */}
            <ImageViewer 
              images={getModuleImages()} 
              onImageViewed={handleImageViewed}
              viewedImages={imagesViewed}
            />
            
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
              <div className="text-center mb-3">
                <div className="text-2xl mb-2">📚</div>
                <h4 className="font-bold text-green-900 mb-1">Lese-Fortschritt</h4>
                <p className="text-green-700 text-sm mb-3">
                  {hasReadToBottom 
                    ? 'Inhalt vollständig gelesen! Sie können das Modul jetzt abschließen.' 
                    : 'Scrollen Sie nach unten, um den gesamten Inhalt zu lesen.'}
                </p>
                {getProgressIndicator()}
              </div>
            </div>
          </div>
        );
        
      case 'video':
        const videoUrl = module.video_url || module.content;
        if (videoUrl && (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be'))) {
          const videoId = videoUrl.includes('youtu.be') 
            ? videoUrl.split('/').pop() 
            : videoUrl.split('v=')[1]?.split('&')[0];
          
          const handleYouTubeProgress = (percentage, currentTime, duration) => {
            console.log('YouTube Progress:', percentage, currentTime, duration); // Debug log
            setVideoProgress(percentage);
            setVideoDuration(duration);
            
            // Video is considered fully watched at 95% completion
            if (percentage >= 95 && !videoFullyWatched) {
              console.log('Video 95% reached, marking as fully watched'); // Debug log
              setVideoFullyWatched(true);
            }
          };
          
          const handleYouTubeReady = (event) => {
            console.log('YouTube Player ready:', event); // Debug log
          };
          
          const handleYouTubeEnd = () => {
            console.log('YouTube Video ended'); // Debug log
            setVideoFullyWatched(true);
            setVideoProgress(100);
          };
          
          return (
            <div className="space-y-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <div className="text-4xl mb-4">🎥</div>
                <h3 className="text-xl font-bold text-red-900 mb-2">Video-Lektion</h3>
                <p className="text-red-700">Schauen Sie sich das Video vollständig an, um das Modul abzuschließen</p>
              </div>
              
              {/* Main Content First - Regular Video */}
              <div className="aspect-video rounded-lg overflow-hidden border border-gray-200 shadow-lg">
                <YouTubePlayer
                  videoId={videoId}
                  onProgress={handleYouTubeProgress}
                  onReady={handleYouTubeReady}
                  onEnd={handleYouTubeEnd}
                  className="w-full"
                />
              </div>
              
              <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg p-4">
                <div className="text-center mb-3">
                  <div className="text-2xl mb-2">📊</div>
                  <h4 className="font-bold text-red-900 mb-1">Video-Fortschritt</h4>
                  <p className="text-red-700 text-sm mb-3">
                    {videoFullyWatched 
                      ? 'Video vollständig angesehen! Sie können das Modul jetzt abschließen.' 
                      : `Video-Fortschritt: ${videoProgress.toFixed(1)}% (mindestens 95% erforderlich)`}
                  </p>
                  
                  {/* Single unified progress bar */}
                  <div className="mt-4 bg-gray-200 rounded-full h-4 relative overflow-hidden">
                    <div 
                      className="bg-red-500 h-4 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${Math.min(videoProgress, 100)}%` }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xs font-medium text-white drop-shadow-md">
                        {videoProgress.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  
                  {videoDuration > 0 && (
                    <div className="mt-2 text-xs text-gray-600">
                      Aktuelle Zeit: {Math.floor((videoProgress / 100) * videoDuration / 60)}:{String(Math.floor(((videoProgress / 100) * videoDuration) % 60)).padStart(2, '0')} / {Math.floor(videoDuration / 60)}:{String(Math.floor(videoDuration % 60)).padStart(2, '0')}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Module Images - After Main Content */}
              <ImageViewer 
                images={getModuleImages()} 
                onImageViewed={handleImageViewed}
                viewedImages={imagesViewed}
              />
            </div>
          );
        } else if (videoUrl) {
          return (
            <div className="space-y-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <div className="text-4xl mb-4">🎥</div>
                <h3 className="text-xl font-bold text-red-900 mb-2">Video-Lektion</h3>
                <p className="text-red-700">Schauen Sie sich das Video vollständig an, um das Modul abzuschließen</p>
              </div>
              
              {/* Main Content First - Regular Video */}
              <div className="rounded-lg overflow-hidden border border-gray-200 shadow-lg">
                <video 
                  controls 
                  className="w-full rounded-lg"
                  onTimeUpdate={handleVideoTimeUpdate}
                  onLoadedMetadata={handleVideoLoadedMetadata}
                  onEnded={handleVideoEnded}
                >
                  <source src={videoUrl} type="video/mp4" />
                  Ihr Browser unterstützt keine Videos.
                </video>
              </div>
              
              {/* Module Images - After Main Content */}
              <ImageViewer 
                images={getModuleImages()} 
                onImageViewed={handleImageViewed}
                viewedImages={imagesViewed}
              />
              
              <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg p-4">
                <div className="text-center mb-3">
                  <div className="text-2xl mb-2">📊</div>
                  <h4 className="font-bold text-red-900 mb-1">Video-Fortschritt</h4>
                  <p className="text-red-700 text-sm mb-3">
                    {videoFullyWatched 
                      ? 'Video vollständig angesehen! Sie können das Modul jetzt abschließen.' 
                      : `Bitte schauen Sie das Video vollständig an. Fortschritt: ${Math.round(videoProgress)}%`}
                  </p>
                  {getProgressIndicator()}
                </div>
              </div>
            </div>
          );
        }
        return (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎥</div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">Kein Video verfügbar</h3>
            <p className="text-gray-500">Für dieses Modul wurde noch kein Video hochgeladen.</p>
          </div>
        );
        
      case 'quiz':
        const quizData = { questions: getQuizQuestions(module) };
        
        // Quiz failed - show failure message and reset option
        if (quizEvaluated && !quizPassed) {
          return (
            <div className="space-y-8">
              <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-lg p-6 text-center">
                <div className="text-4xl mb-4">❌</div>
                <h3 className="text-xl font-bold text-red-900 mb-2">Quiz nicht bestanden</h3>
                <p className="text-red-700 mb-4">
                  Du hast leider nicht bestanden. Bitte wiederhole das Modul, um fortzufahren.
                </p>
                <p className="text-red-600 text-sm mb-4">
                  Ergebnis: {quizScore}/{quizTotal} korrekt ({quizTotal > 0 ? ((quizScore / quizTotal) * 100).toFixed(1) : 0}%) - mindestens {getPassingPercent(module)}% erforderlich
                </p>
                <button
                  onClick={resetQuiz}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  Quiz wiederholen
                </button>
              </div>
            </div>
          );
        }
        
        // Quiz passed - show detailed results
        if (quizEvaluated && quizPassed && quizShowResults) {
          return (
            <div className="space-y-8">
              <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-6 text-center">
                <div className="text-4xl mb-4">✅</div>
                <h3 className="text-xl font-bold text-green-900 mb-2">Quiz erfolgreich bestanden!</h3>
                <p className="text-green-700 mb-2">
                  Herzlichen Glückwunsch! Das Modul wurde erfolgreich abgeschlossen.
                </p>
                <p className="text-green-600 text-sm">
                  Ergebnis: {quizScore}/{quizTotal} korrekt ({quizTotal > 0 ? ((quizScore / quizTotal) * 100).toFixed(1) : 0}%)
                </p>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">📘 Lösungsübersicht</h4>
                
                {quizData.questions?.map((question, index) => {
                  const userAnswer = quizAnswers[index] || [];
                  const correctIndices = getCorrectIndices(question);
                  const isCorrect = isAnswerCorrect(question, userAnswer);
                  
                  return (
                    <div key={index} className="mb-6 p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center mb-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3 ${
                          isCorrect ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                        }`}>
                          {isCorrect ? '✓' : '✗'}
                        </div>
                        <h5 className="text-md font-medium text-gray-900">{question.question}</h5>
                      </div>
                      
                      <div className="ml-11 space-y-2">
                        {question.options?.map((option, optIndex) => {
                          const isUserAnswer = userAnswer.includes(optIndex);
                          const isCorrectAnswer = correctIndices.includes(optIndex);
                          
                          return (
                            <div key={optIndex} className={`p-2 rounded-lg ${
                              isCorrectAnswer 
                                ? 'bg-green-100 border border-green-300' 
                                : isUserAnswer 
                                  ? 'bg-red-100 border border-red-300' 
                                  : 'bg-gray-50'
                            }`}>
                              <div className="flex items-center">
                                <span className={`inline-block w-6 h-6 rounded-full text-xs font-bold text-center leading-6 mr-2 ${
                                  isCorrectAnswer 
                                    ? 'bg-green-500 text-white' 
                                    : isUserAnswer 
                                      ? 'bg-red-500 text-white' 
                                      : 'bg-gray-300 text-gray-600'
                                }`}>
                                  {String.fromCharCode(65 + optIndex)}
                                </span>
                                <span className={`${
                                  isCorrectAnswer 
                                    ? 'text-green-800 font-medium' 
                                    : isUserAnswer 
                                      ? 'text-red-800' 
                                      : 'text-gray-600'
                                }`}>
                                  {option}
                                </span>
                                {isCorrectAnswer && (
                                  <span className="ml-2 text-green-600 text-sm">✓ Richtig</span>
                                )}
                                {isUserAnswer && !isCorrectAnswer && (
                                  <span className="ml-2 text-red-600 text-sm">👤 Ihre Antwort</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        
                        {question.explanation && (
                          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center text-blue-800 text-sm font-medium mb-1">
                              <span className="mr-1">💡</span>
                              Erklärung:
                            </div>
                            <p className="text-blue-700 text-sm">{question.explanation}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }
        
        // Normal quiz view - no immediate feedback
        return (
          <div className="space-y-8">
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6 text-center">
              <div className="text-4xl mb-4">🧠</div>
              <h3 className="text-xl font-bold text-purple-900 mb-2">Quiz Zeit!</h3>
              <p className="text-purple-700">Testen Sie Ihr Wissen mit {quizData.questions?.length || 0} {quizData.questions?.length === 1 ? 'Frage' : 'Fragen'}</p>
              <p className="text-purple-600 text-sm mt-2 font-medium">
                📊 Mindestens {getPassingPercent(module)}% korrekte Antworten erforderlich zum Abschluss
              </p>
            </div>
            
            {/* Main Content First - Quiz Questions */}
            {quizData.questions?.map((question, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="bg-gradient-to-r from-purple-500 to-blue-500 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                    {index + 1}
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">{question.question}</h4>
                </div>
                
                {/* Question Image (if available) */}
                {question.image && (
                  <div className="mb-6 ml-11">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <span className="text-blue-600 text-sm font-medium">🖼️ Bezugsbild:</span>
                        <span className="ml-2 text-blue-800 text-sm">{question.image.title}</span>
                      </div>
                      <img 
                        src={question.image.url} 
                        alt={question.image.alt}
                        className="w-full max-w-md max-h-60 object-contain rounded-lg border border-gray-200 shadow-sm"
                      />
                      {question.image.description && (
                        <p className="text-blue-700 text-sm mt-2">{question.image.description}</p>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="space-y-3 ml-11">
                  {question.options?.map((option, optIndex) => {
                    const questionType = question.type || 'single';
                    
                    // Fix selection logic for both single and multiple choice
                    let isSelected = false;
                    if (questionType === 'multiple') {
                      isSelected = (quizAnswers[index] || []).includes(optIndex);
                    } else {
                      // Single choice: check if this option index is in the answers array
                      const userAnswers = quizAnswers[index] || [];
                      isSelected = Array.isArray(userAnswers) 
                        ? userAnswers.includes(optIndex)
                        : userAnswers === optIndex; // Legacy compatibility
                    }
                    
                    return (
                      <label 
                        key={optIndex} 
                        className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-all group"
                      >
                        <div className="relative">
                          <input 
                            type={questionType === 'multiple' ? 'checkbox' : 'radio'}
                            name={`question-${index}`} 
                            value={optIndex}
                            checked={isSelected}
                            onChange={() => handleQuizAnswer(index, optIndex, questionType)}
                            className="sr-only"
                          />
                          <div className={`w-5 h-5 border-2 border-gray-300 group-hover:border-blue-400 transition-colors ${
                            questionType === 'multiple' ? 'rounded' : 'rounded-full'
                          }`}>
                            {isSelected && (
                              <div className={`w-full h-full bg-blue-500 transition-transform ${
                                questionType === 'multiple' 
                                  ? 'rounded flex items-center justify-center' 
                                  : 'rounded-full scale-50'
                              }`}>
                                {questionType === 'multiple' && (
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <span className="ml-3 text-gray-700 group-hover:text-blue-700 font-medium">
                          <span className="inline-block w-6 h-6 bg-gray-100 group-hover:bg-blue-100 rounded-full text-xs font-bold text-center leading-6 mr-2">
                            {String.fromCharCode(65 + optIndex)}
                          </span>
                          {typeof option === 'object' ? option.text : option}
                        </span>
                      </label>
                    );
                  })}
                  
                  {/* Show question type hint */}
                  <div className="text-xs text-gray-500 mt-2 italic">
                    {question.type === 'multiple' 
                      ? '💡 Mehrere Antworten möglich - wählen Sie alle zutreffenden aus'
                      : '💡 Nur eine Antwort möglich'
                    }
                  </div>
                </div>
                
                {/* NO immediate explanation - only show after completion */}
              </div>
            )) || (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📝</div>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">Keine Quiz-Fragen verfügbar</h3>
                <p className="text-gray-500">Für dieses Modul wurden noch keine Fragen erstellt.</p>
              </div>
            )}
            
            {/* Module Images - After Main Content */}
            <ImageViewer 
              images={getModuleImages()} 
              onImageViewed={handleImageViewed}
              viewedImages={imagesViewed}
            />
            
            {quizData.questions?.length > 0 && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
                <div className="text-center mb-3">
                  <div className="text-3xl mb-3">🎯</div>
                  <h4 className="font-bold text-green-900 mb-2">Quiz-Fortschritt</h4>
                  <p className="text-green-700 text-sm mb-3">
                    {(() => {
                      const allAnswered = Object.keys(quizAnswers).length >= quizData.questions.length;
                      
                      if (allAnswered) {
                        return 'Alle Fragen beantwortet. Klicken Sie auf "Modul abschließen" für die Auswertung.';
                      } else {
                        return 'Beantworten Sie alle Fragen, um das Modul abzuschließen.';
                      }
                    })()}
                  </p>
                  {getProgressIndicator()}
                </div>
              </div>
            )}
          </div>
        );
        
      case 'document':
      case 'pdf':
        // PDF SLIDES - Load from DB (converted at upload time)
        if (module.content_type === 'pdf') {
          // Priority 1: Show slides if available (even while other data loads)
          if (pdfSlides.length > 0) {
            const currentSlideData = pdfSlides[currentSlide - 1];
            
            return (
              <div>
                {/* Compact Header */}
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm text-gray-600">
                    {slidesViewed.has(currentSlide) ? '✅ Angesehen' : '⏱️ Wird angesehen...'}
                  </p>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-28 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${(slidesViewed.size / pdfSlides.length) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 font-medium">{slidesViewed.size}/{pdfSlides.length}</span>
                    </div>
                    <button
                      onClick={() => setIsFullscreen(true)}
                      className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Vollbild öffnen"
                      data-testid="fullscreen-btn"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Slide Image - with zoom support */}
                {currentSlideData && (
                  <div className="relative flex items-center justify-center bg-gray-50 rounded-lg overflow-auto" style={{ minHeight: '60vh' }}>
                    {/* Zoom Controls */}
                    <div className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-lg shadow px-2 py-1.5 border border-gray-200">
                      <button
                        onClick={() => setSlideZoom(z => Math.max(1, parseFloat((z - 0.25).toFixed(2))))}
                        disabled={slideZoom <= 1}
                        title="Verkleinern"
                        className="w-7 h-7 flex items-center justify-center rounded text-gray-600 hover:bg-gray-100 disabled:opacity-30 text-base font-bold"
                      >−</button>
                      <span className="text-xs font-medium text-gray-600 w-8 text-center select-none">
                        {Math.round(slideZoom * 100)}%
                      </span>
                      <button
                        onClick={() => setSlideZoom(z => Math.min(3, parseFloat((z + 0.25).toFixed(2))))}
                        disabled={slideZoom >= 3}
                        title="Vergrößern"
                        className="w-7 h-7 flex items-center justify-center rounded text-gray-600 hover:bg-gray-100 disabled:opacity-30 text-base font-bold"
                      >+</button>
                      {slideZoom !== 1 && (
                        <button
                          onClick={() => setSlideZoom(1)}
                          title="Zurücksetzen"
                          className="w-7 h-7 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 text-xs font-medium"
                        >1:1</button>
                      )}
                    </div>
                    <div
                      style={{
                        transform: `scale(${slideZoom})`,
                        transformOrigin: 'top center',
                        transition: 'transform 0.2s ease',
                        marginBottom: slideZoom > 1 ? `${(slideZoom - 1) * 100}%` : 0,
                      }}
                    >
                      <img
                        src={currentSlideData.slide_image}
                        alt={`Slide ${currentSlide}`}
                        className="max-w-full h-auto rounded-lg shadow-lg"
                        style={{ maxHeight: '75vh', objectFit: 'contain' }}
                        data-testid="slide-image"
                      />
                    </div>
                  </div>
                )}

                {/* Compact Navigation */}
                <div className="flex justify-between items-center mt-2 py-2">
                  <button
                    onClick={goToPreviousSlide}
                    disabled={currentSlide <= 1}
                    data-testid="prev-slide-btn"
                    className={`flex items-center space-x-1 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                      currentSlide <= 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-600 hover:bg-gray-700 text-white'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span>Zurück</span>
                  </button>
                  
                  {/* Clickable Progress Dots */}
                  <div className="text-center">
                    <div className="flex space-x-1.5 justify-center">
                      {pdfSlides.map((_, index) => {
                        const slideNum = index + 1;
                        const isViewed = slidesViewed.has(slideNum);
                        const isCurrent = slideNum === currentSlide;
                        const canNavigate = slideNum < currentSlide || isViewed || (canProceedToNextSlide && slideNum === currentSlide + 1);
                        return (
                        <button
                          key={index}
                          onClick={() => {
                            if (!canNavigate || isCurrent) return;
                            setCurrentSlide(slideNum);
                            if (!isViewed) {
                              setCanProceedToNextSlide(false);
                              setSlideTimer(30);
                              setSlideTimerActive(true);
                            }
                          }}
                          disabled={!canNavigate || isCurrent}
                          data-testid={`slide-dot-${slideNum}`}
                          className={`w-3 h-3 rounded-full transition-all duration-300 ${
                            isCurrent
                              ? 'bg-blue-500 ring-2 ring-blue-200 scale-125'
                              : isViewed
                              ? 'bg-green-500 hover:bg-green-400 cursor-pointer hover:scale-150'
                              : canNavigate
                              ? 'bg-gray-400 hover:bg-gray-500 cursor-pointer hover:scale-150'
                              : 'bg-gray-300 cursor-not-allowed'
                          }`}
                          title={`Seite ${slideNum}`}
                        />
                        );
                      })}
                    </div>
                    {slideTimer > 0 && slideTimerActive && (
                      <div className="flex items-center justify-center space-x-1 text-xs mt-1">
                        <div className="w-3 h-3 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-orange-600 font-medium">Noch {slideTimer}s</span>
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={goToNextSlide}
                    disabled={!canProceedToNextSlide}
                    data-testid="next-slide-btn"
                    className={`flex items-center space-x-1 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                      canProceedToNextSlide
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <span>{currentSlide >= pdfSlides.length ? (onComplete ? 'Abschließen' : 'Schließen') : 'Weiter'}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {/* Progress Indicator */}
                {getProgressIndicator()}
              </div>
            );
          }
          
          // No slides yet - show loading or error
          return (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              {slideLoadError ? (
                <>
                  <p className="text-lg font-medium text-gray-700">Slides konnten nicht geladen werden</p>
                  <button
                    onClick={() => {
                      slidesCache.delete(module.id);
                      setSlideLoadError(false);
                      setPdfSlides([]);
                      // Re-trigger the useEffect by forcing a re-fetch
                      const token = localStorage.getItem('token');
                      if (token) {
                        fetch(`${API}/modules/${module.id}/pdf-slides`, {
                          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                        }).then(r => r.json()).then(data => {
                          if (data.slides && data.slides.length > 0) {
                            slidesCache.set(module.id, data.slides);
                            setPdfSlides(data.slides);
                            setCurrentSlide(1);
                            setSlidesViewed(new Set());
                            setSlideTimer(30);
                            setSlideTimerActive(true);
                          } else {
                            setSlideLoadError(true);
                          }
                        }).catch(() => setSlideLoadError(true));
                      }
                    }}
                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                    data-testid="retry-slides-btn"
                  >
                    Erneut versuchen
                  </button>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                  <p className="text-lg font-medium text-gray-700">Slides werden geladen...</p>
                </>
              )}
            </div>
          );
        }

        // Regular document handling (non-PDF)
        const docUrl = module.document_url || module.content;
        console.log('Document URL:', docUrl); // Debug log
        
        if (docUrl) {
          // Fix URL to point to backend API for uploaded files
          let correctedUrl = docUrl;
          if (docUrl.startsWith('/uploads/')) {
            // Extract filename and use API endpoint
            const filename = docUrl.split('/').pop();
            correctedUrl = `${API}/files/${filename}`;
            console.log('Corrected URL via API:', correctedUrl);
          }
          
          // Function to handle direct download without navigation
          const handleDirectDownload = async (docUrl, filename) => {
            try {
              let downloadUrl = docUrl;
              
              // If it's an uploaded file, use the dedicated download endpoint
              if (docUrl.startsWith('/uploads/')) {
                const file = docUrl.split('/').pop();
                downloadUrl = `${API}/download/${file}`;
              }
              
              console.log('Starting download from:', downloadUrl);
              
              // Use fetch to download the file
              const response = await fetch(downloadUrl, {
                method: 'GET',
                headers: {
                  'Cache-Control': 'no-cache'
                }
              });
              
              if (!response.ok) {
                throw new Error(`Download failed: ${response.status} ${response.statusText}`);
              }
              
              // Get the blob and create download link
              const blob = await response.blob();
              const blobUrl = window.URL.createObjectURL(blob);
              
              // Create temporary download link
              const link = document.createElement('a');
              link.href = blobUrl;
              link.download = filename || 'document.pdf';
              link.style.display = 'none';
              
              // Add to DOM, click, and remove
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              // Clean up blob URL
              setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
              
              console.log('Download completed successfully');
            } catch (error) {
              console.error('Download error:', error);
              
              // Fallback: try using download endpoint directly
              let fallbackUrl = docUrl;
              if (docUrl.startsWith('/uploads/')) {
                const file = docUrl.split('/').pop();
                fallbackUrl = `${API}/download/${file}`;
              }
              
              // Open in new window as last resort
              window.open(fallbackUrl, '_blank');
            }
          };
          
          // Extract filename for download
          const downloadFilename = docUrl.split('/').pop() || 'document.pdf';
          
          // Check if it's a valid URL
          const isValidUrl = correctedUrl.startsWith('http://') || correctedUrl.startsWith('https://') || correctedUrl.startsWith('/');
          
          return (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="text-4xl mr-4">📄</div>
                  <div>
                    <h4 className="font-bold text-blue-900 text-lg">Dokument verfügbar</h4>
                    <p className="text-blue-800">Klicken Sie unten, um das Dokument zu öffnen oder laden Sie es herunter.</p>
                  </div>
                </div>
                
                {/* Main Content First - Document/PDF */}
                <div className="flex gap-3">
                  <a
                    href={correctedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-flex items-center"
                  >
                    <span className="mr-2">🔗</span>
                    Dokument öffnen
                  </a>
                  
                  <button
                    onClick={() => handleDirectDownload(docUrl, downloadFilename)}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-flex items-center"
                  >
                    <span className="mr-2">⬇️</span>
                    Download starten
                  </button>
                </div>
              </div>
              
              {/* Only show iframe for valid URLs and PDFs */}
              {isValidUrl && (correctedUrl.toLowerCase().includes('.pdf') || docUrl.toLowerCase().includes('.pdf')) && (
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-4 py-2 text-sm text-gray-600 font-medium">
                    📄 PDF-Vorschau
                  </div>
                  <div className="relative">
                    <iframe
                      src={`${correctedUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                      className="w-full h-96 border-0"
                      title={module.title}
                      onLoad={handlePdfLoad}
                      onError={(e) => {
                        console.error('PDF iframe error:', e);
                        console.error('Failed URL:', correctedUrl);
                        // Show fallback
                        const fallback = document.getElementById(`pdf-fallback-${module.id}`);
                        if (fallback) {
                          e.target.style.display = 'none';
                          fallback.classList.remove('hidden');
                        }
                      }}
                    />
                    <div className="absolute inset-0 bg-white rounded-lg p-8 text-center hidden" id={`pdf-fallback-${module.id}`}>
                      <div className="text-4xl mb-4">⚠️</div>
                      <h4 className="font-bold text-gray-700 mb-2">PDF-Vorschau nicht verfügbar</h4>
                      <p className="text-gray-600 mb-4">Die PDF kann nicht in der Vorschau angezeigt werden.</p>
                      <a
                        href={correctedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors inline-block"
                      >
                        PDF direkt öffnen
                      </a>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Module Images - After Main Content */}
              <ImageViewer 
                images={getModuleImages()} 
                onImageViewed={handleImageViewed}
                viewedImages={imagesViewed}
              />
              
              {/* PDF Reading Progress */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                <div className="text-center mb-3">
                  <div className="text-2xl mb-2">📊</div>
                  <h4 className="font-bold text-blue-900 mb-1">Lese-Fortschritt</h4>
                  <p className="text-blue-700 text-sm mb-3">
                    {pdfFullyViewed 
                      ? 'PDF vollständig geladen! Sie können das Modul jetzt abschließen.' 
                      : 'PDF wird geladen... Bitte warten Sie bis das Dokument vollständig angezeigt wird.'}
                  </p>
                  {getProgressIndicator()}
                </div>
              </div>
              
              {/* For non-PDF documents */}
              {isValidUrl && !correctedUrl.toLowerCase().includes('.pdf') && !docUrl.toLowerCase().includes('.pdf') && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center text-amber-800">
                    <span className="text-2xl mr-3">📎</span>
                    <div>
                      <h5 className="font-medium">Nicht-PDF Dokument</h5>
                      <p className="text-sm">Dieses Dokument kann nicht in der Vorschau angezeigt werden. Klicken Sie auf "Dokument öffnen" um es anzuzeigen.</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Debug information (remove in production) */}
              <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 text-xs">
                <strong>Debug Info:</strong><br/>
                Original URL: {docUrl}<br/>
                Corrected URL: {correctedUrl}<br/>
                Download Filename: {downloadFilename}<br/>
                Download Endpoint: {docUrl.startsWith('/uploads/') ? `${API}/download/${docUrl.split('/').pop()}` : 'External URL'}<br/>
                API Base: {API}
              </div>
            </div>
          );
        }
        return (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📄</div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">Kein Dokument verfügbar</h3>
            <p className="text-gray-500">Für dieses Modul wurde noch kein Dokument hochgeladen.</p>
          </div>
        );
        
      default:
        return <p className="text-gray-500">Unbekannter Inhaltstyp.</p>;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-xl shadow-xl max-w-7xl w-full max-h-[95vh] overflow-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{module.title}</h2>
            <p className="text-gray-600 mt-1">{module.description}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>
        
        <div className="p-3">
          {renderContent()}
        </div>
        
        <div className="p-6 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={onClose}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Schließen
              </button>
              
              {/* Progress indicator in bottom bar */}
              {getProgressIndicator()}
            </div>
            
            <div className="flex items-center space-x-4">
              {!isCompleted && (
                <div className="flex flex-col items-end">
                  <button
                    onClick={() => {
                      console.log('🎯 COMPLETION BUTTON CLICKED! Module:', module.title);
                      console.log('🔍 Can complete?', canComplete());
                      console.log('🔍 Module completion status:', module.completion_status);
                      handleCompletionAttempt();
                    }}
                    disabled={!canComplete() || isPreviewMode}
                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                      isPreviewMode
                        ? 'bg-purple-100 text-purple-700 cursor-default'
                        : canComplete() 
                          ? 'bg-green-600 hover:bg-green-700 text-white' 
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    } ${
                      // Hide for PDF slides – the slide nav "Abschließen" button already handles completion
                      (module.content_type === 'pdf' && pdfSlides.length > 0) ? 'hidden' : ''
                    }`}
                  >
                    {getCompletionButtonText()}
                  </button>
                  
                  {/* Completion requirement tooltip */}
                  {!canComplete() && (
                    <div className="mt-2 text-sm text-gray-600 text-right max-w-xs">
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="flex items-center text-yellow-800 text-xs font-medium mb-1">
                          <span className="mr-1">⚠️</span>
                          Hinweis:
                        </div>
                        <p className="text-yellow-700 text-xs">
                          {getCompletionRequirement()}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Warning message when user tries to complete prematurely */}
                  {showCompletionWarning && (
                    <div className="mt-2 text-sm text-red-600 text-right max-w-xs">
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div className="flex items-center text-red-800 text-xs font-medium mb-1">
                          <span className="mr-1">❗</span>
                          Abschluss nicht möglich:
                        </div>
                        <p className="text-red-700 text-xs">
                          Bitte schließen Sie das Modul erst ab, wenn Sie alle Inhalte vollständig gelesen, angesehen oder bearbeitet haben.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {isCompleted && (
                <div className="flex items-center space-x-2 text-green-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-semibold">Bereits abgeschlossen</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Fullscreen Modal for PDF Slides (OLD - keeping for backward compatibility) */}
      {isFullscreen && module.content_type === 'pdf' && pdfSlides.length > 0 && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
          {/* Slim Fullscreen Header */}
          <div className="bg-black/90 text-white px-4 py-2 flex justify-between items-center border-b border-gray-800">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-gray-300">{module.title}</span>
              <span className="text-xs text-gray-500">|</span>
              <span className="text-sm text-gray-400">{currentSlide}/{pdfSlides.length}</span>
              <div className="w-32 bg-gray-700 rounded-full h-1.5">
                <div 
                  className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${(currentSlide / pdfSlides.length) * 100}%` }}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Zoom Controls */}
              <div className="flex items-center gap-1 bg-white/10 rounded-lg px-2 py-1">
                <button
                  onClick={() => setSlideZoom(z => Math.max(1, parseFloat((z - 0.25).toFixed(2))))}
                  disabled={slideZoom <= 1}
                  title="Verkleinern"
                  className="w-6 h-6 flex items-center justify-center rounded text-gray-300 hover:text-white hover:bg-white/10 disabled:opacity-30 text-base font-bold"
                >−</button>
                <span className="text-xs font-medium text-gray-300 w-8 text-center select-none">
                  {Math.round(slideZoom * 100)}%
                </span>
                <button
                  onClick={() => setSlideZoom(z => Math.min(3, parseFloat((z + 0.25).toFixed(2))))}
                  disabled={slideZoom >= 3}
                  title="Vergrößern"
                  className="w-6 h-6 flex items-center justify-center rounded text-gray-300 hover:text-white hover:bg-white/10 disabled:opacity-30 text-base font-bold"
                >+</button>
                {slideZoom !== 1 && (
                  <button
                    onClick={() => setSlideZoom(1)}
                    title="Zurücksetzen"
                    className="text-xs text-gray-400 hover:text-white px-1 font-medium"
                  >1:1</button>
                )}
              </div>
              <button
                onClick={() => setIsFullscreen(false)}
                className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                title="Schließen (ESC)"
                data-testid="fullscreen-close-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Maximized Slide Content - with zoom support */}
          <div className="flex-1 flex items-start justify-center p-2 overflow-auto">
            {pdfSlides[currentSlide - 1] && (
              <div style={{
                transform: `scale(${slideZoom})`,
                transformOrigin: 'top center',
                transition: 'transform 0.2s ease',
                marginBottom: slideZoom > 1 ? `${(slideZoom - 1) * 80}vh` : 0,
              }}>
                <img
                  src={pdfSlides[currentSlide - 1].slide_image}
                  alt={`Slide ${currentSlide}`}
                  className="max-w-full object-contain"
                  style={{ maxHeight: 'calc(100vh - 110px)', maxWidth: '96vw' }}
                  data-testid="fullscreen-slide-image"
                />
              </div>
            )}
          </div>
          
          {/* Compact Fullscreen Navigation */}
          <div className="bg-black/95 text-white px-4 py-2 border-t border-gray-800">
            <div className="flex justify-between items-center max-w-5xl mx-auto">
              <button
                onClick={() => { if (currentSlide > 1) setCurrentSlide(prev => prev - 1); }}
                disabled={currentSlide <= 1}
                data-testid="fullscreen-prev-btn"
                className={`flex items-center space-x-2 px-5 py-2.5 rounded-lg font-medium transition-colors ${
                  currentSlide <= 1
                    ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                    : 'bg-gray-700 hover:bg-gray-600 text-white'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Zurück</span>
              </button>
              
              {/* Clickable Dots + Timer */}
              <div className="flex flex-col items-center space-y-1">
                <div className="flex space-x-2">
                  {pdfSlides.map((_, index) => {
                    const slideNum = index + 1;
                    const isViewed = slidesViewed.has(slideNum);
                    const isCurrent = slideNum === currentSlide;
                    const canNavigate = slideNum < currentSlide || isViewed || (canProceedToNextSlide && slideNum === currentSlide + 1);
                    return (
                    <button
                      key={index}
                      onClick={() => {
                        if (!canNavigate || isCurrent) return;
                        setCurrentSlide(slideNum);
                        if (!isViewed) {
                          setCanProceedToNextSlide(false);
                          setSlideTimer(30);
                          setSlideTimerActive(true);
                        }
                        const newViewed = new Set(slidesViewed);
                        newViewed.add(slideNum);
                        setSlidesViewed(newViewed);
                      }}
                      disabled={!canNavigate || isCurrent}
                      data-testid={`fullscreen-dot-${slideNum}`}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        isCurrent
                          ? 'bg-blue-400 ring-2 ring-blue-400/40 scale-125'
                          : isViewed
                          ? 'bg-green-500 hover:bg-green-400 cursor-pointer hover:scale-150'
                          : canNavigate
                          ? 'bg-gray-500 hover:bg-gray-400 cursor-pointer hover:scale-150'
                          : 'bg-gray-700 cursor-not-allowed'
                      }`}
                      title={`Seite ${slideNum}`}
                    />
                    );
                  })}
                </div>
                {slideTimerActive && slideTimer > 0 ? (
                  <span className="text-xs text-yellow-400">Noch {slideTimer}s</span>
                ) : canProceedToNextSlide ? (
                  <span className="text-xs text-green-400">Bereit</span>
                ) : null}
              </div>
              
              <button
                onClick={() => {
                  if (canProceedToNextSlide && currentSlide < pdfSlides.length) {
                    setCurrentSlide(prev => prev + 1);
                    setCanProceedToNextSlide(false);
                    setSlideTimer(30);
                    setSlideTimerActive(true);
                    const newViewed = new Set(slidesViewed);
                    newViewed.add(currentSlide + 1);
                    setSlidesViewed(newViewed);
                    setAllSlidesViewed(newViewed.size >= pdfSlides.length);
                  }
                }}
                disabled={!canProceedToNextSlide}
                data-testid="fullscreen-next-btn"
                className={`flex items-center space-x-2 px-5 py-2.5 rounded-lg font-medium transition-colors ${
                  !canProceedToNextSlide
                    ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                    : currentSlide >= pdfSlides.length
                    ? 'bg-green-600 hover:bg-green-500 text-white'
                    : 'bg-blue-600 hover:bg-blue-500 text-white'
                }`}
              >
                <span>{currentSlide >= pdfSlides.length ? (onComplete ? 'Abschließen' : 'Schließen') : 'Weiter'}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { prefetchModuleSlides };
export default ModuleViewer;
